import { trpc } from "@/api/trpc";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { cn } from "@/shared/lib/utils";
import { keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Search } from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

/** Hledá se až od 2 znaků — jednopísmenný dotaz by vracel půlku databáze. */
const MIN_QUERY_LENGTH = 2;

const DATE_FMT = new Intl.DateTimeFormat("cs-CZ", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
});

const CURRENCY_FMT = new Intl.NumberFormat("cs-CZ", {
  style: "currency",
  currency: "CZK",
  maximumFractionDigits: 0,
});

function formatKc(value: string): string {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? CURRENCY_FMT.format(n) : value;
}

interface SearchItem {
  key: string;
  label: string;
  sublabel: string | null;
  testId: string;
  /** Zrušený termín — přeškrtnutý label + pill „Zrušen" ve výsledcích. */
  zrusen?: boolean;
  onSelect: () => void;
}

interface SearchCategory {
  title: string;
  items: SearchItem[];
}

interface GlobalSearchProps {
  /** Automatický fokus inputu — mobilní overlay otevřený lupou. */
  autoFocus?: boolean | undefined;
  /** Zavolá se po výběru výsledku — mobilní overlay se tím zavře. */
  onNavigate?: (() => void) | undefined;
}

/**
 * Globální vyhledávač v horní liště — dropdown pod inputem s výsledky ve
 * čtyřech kategoriích (klienti, firmy, termíny, faktury). Debounce 250 ms,
 * klávesnice (šipky + Enter, Esc zavře), klik naviguje na detail/výpis.
 * Kategorie bez oprávnění server tiše vynechá. Na mobilu žije ve fullscreen
 * overlay (viz TopBar) — `onNavigate` ho po výběru zavře.
 */
export function GlobalSearch({ autoFocus, onNavigate }: GlobalSearchProps = {}) {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Enter stisknutý během fetche — dotaz, pro který má po doběhnutí
  // proběhnout navigace na první výsledek.
  const [pendingEnter, setPendingEnter] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounced = useDebouncedValue(value.trim(), 250);
  const enabled = debounced.length >= MIN_QUERY_LENGTH;

  const searchQuery = trpc.hca.search.global.useQuery(
    { q: debounced },
    { enabled, retry: false, placeholderData: keepPreviousData },
  );

  const categories = useMemo<SearchCategory[]>(() => {
    const data = searchQuery.data;
    if (!data) return [];
    const cats: SearchCategory[] = [];
    if (data.klienti.length > 0) {
      cats.push({
        title: "Klienti",
        items: data.klienti.map((k) => ({
          key: `klient-${k.id}`,
          label: `${k.jmeno}${k.prijmeni ? ` ${k.prijmeni}` : ""}`,
          sublabel: [k.email, k.firmaNazev].filter(Boolean).join(" · ") || null,
          testId: `global-search-klient-${k.id}`,
          onSelect: () =>
            navigate({ to: "/dashboard/klienti/$klientId", params: { klientId: k.id } }),
        })),
      });
    }
    if (data.firmy.length > 0) {
      cats.push({
        title: "Firmy",
        items: data.firmy.map((f) => ({
          key: `firma-${f.id}`,
          label: f.nazev,
          sublabel: f.ico ? `IČO ${f.ico}` : null,
          testId: `global-search-firma-${f.id}`,
          onSelect: () => navigate({ to: "/dashboard/firmy/$firmaId", params: { firmaId: f.id } }),
        })),
      });
    }
    if (data.terminy.length > 0) {
      cats.push({
        title: "Termíny",
        items: data.terminy.map((t) => ({
          key: `termin-${t.id}`,
          label: t.productNazev,
          sublabel: `${DATE_FMT.format(new Date(t.datumOd))} · ${t.misto}`,
          testId: `global-search-termin-${t.id}`,
          zrusen: t.stav === "ZRUSEN",
          onSelect: () =>
            navigate({ to: "/dashboard/terminy/$terminId", params: { terminId: t.id } }),
        })),
      });
    }
    if (data.faktury.length > 0) {
      cats.push({
        title: "Faktury",
        items: data.faktury.map((f) => ({
          key: `faktura-${f.id}`,
          label: f.cislo,
          sublabel: formatKc(f.castkaSDph),
          testId: `global-search-faktura-${f.id}`,
          onSelect: () => navigate({ to: "/dashboard/faktury", search: { cislo: f.cislo } }),
        })),
      });
    }
    return cats;
  }, [searchQuery.data, navigate]);

  const flatItems = useMemo(() => categories.flatMap((c) => c.items), [categories]);

  // Po změně výsledků by starý index ukazoval na jinou položku.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset zvýraznění při nových výsledcích
  useEffect(() => {
    setActiveIndex(-1);
  }, [flatItems]);

  // Klik mimo vyhledávač dropdown zavře. Musí smazat i pendingEnter —
  // jinak by doběhnuvší dotaz odnavigoval už zavřený vyhledávač.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const container = containerRef.current;
      if (container && e.target instanceof Node && !container.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
        setPendingEnter(null);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  function close() {
    setOpen(false);
    setActiveIndex(-1);
    setPendingEnter(null);
  }

  function select(item: SearchItem) {
    close();
    setValue("");
    inputRef.current?.blur();
    item.onSelect();
    onNavigate?.();
  }

  // Enter stisknutý během fetche — po doběhnutí dotazu naviguj na první
  // výsledek. Guard proti stale dotazu: pending platí jen dokud input
  // odpovídá dotazu, pro který byl Enter stisknutý; jinak se zahodí.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `select` se mění každý render — jako dep by efekt spouštěla naprázdno
  useEffect(() => {
    if (pendingEnter === null) return;
    if (pendingEnter !== value.trim()) {
      setPendingEnter(null);
      return;
    }
    if (debounced !== pendingEnter || searchQuery.isFetching) return;
    setPendingEnter(null);
    if (searchQuery.isSuccess) {
      const item = flatItems[0];
      if (item) select(item);
    }
  }, [pendingEnter, value, debounced, searchQuery.isFetching, searchQuery.isSuccess, flatItems]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        close();
      }
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        if (value.trim().length >= MIN_QUERY_LENGTH) setOpen(true);
        return;
      }
      if (flatItems.length === 0) return;
      setActiveIndex((i) =>
        e.key === "ArrowDown" ? (i + 1) % flatItems.length : i <= 0 ? flatItems.length - 1 : i - 1,
      );
      return;
    }
    if (e.key === "Enter") {
      if (!open || value.trim().length < MIN_QUERY_LENGTH) return;
      e.preventDefault();
      // Zobrazené výsledky mohou patřit staršímu dotazu (debounce +
      // keepPreviousData) — Enter se zapamatuje a naviguje na první výsledek,
      // až fetch pro aktuální input doběhne (viz efekt výš).
      if (searchQuery.isFetching || debounced !== value.trim()) {
        setPendingEnter(value.trim());
        return;
      }
      // Enter bez šipek vybere první výsledek.
      const item = flatItems[activeIndex >= 0 ? activeIndex : 0];
      if (item) select(item);
    }
  }

  const isSearching = enabled && searchQuery.isFetching;
  const showEmpty = enabled && searchQuery.isSuccess && flatItems.length === 0;

  return (
    <div ref={containerRef} className="relative w-full">
      {isSearching ? (
        <Loader2
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      ) : (
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      )}
      <Input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="global-search-listbox"
        aria-activedescendant={activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined}
        aria-autocomplete="list"
        aria-label="Globální vyhledávání"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder="Hledat klienta, firmu, termín, fakturu…"
        maxLength={100}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          setValue(v);
          setActiveIndex(-1);
          setOpen(v.trim().length >= MIN_QUERY_LENGTH);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (value.trim().length >= MIN_QUERY_LENGTH) setOpen(true);
        }}
        // Odchod z inputu (tab, klik jinam) ruší zapamatovaný Enter —
        // navigace nesmí přijít poté, co uživatel vyhledávač opustil.
        onBlur={() => setPendingEnter(null)}
        className="h-10 w-full pl-9 pr-3"
        data-testid="app-top-bar-search"
      />

      {open && enabled && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
          data-testid="global-search-dropdown"
        >
          {searchQuery.isError ? (
            <p className="px-3 py-3 text-sm text-muted-foreground" role="alert">
              Vyhledávání selhalo. Zkuste to prosím znovu.
            </p>
          ) : showEmpty ? (
            <p
              className="px-3 py-3 text-sm text-muted-foreground"
              data-testid="global-search-empty"
            >
              Nic nenalezeno
            </p>
          ) : flatItems.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">Hledám…</p>
          ) : (
            <div
              id="global-search-listbox"
              // biome-ignore lint/a11y/useSemanticElements: <select> nelze — nabídka je řízená z inputu (combobox pattern)
              role="listbox"
              tabIndex={-1}
              aria-label="Výsledky vyhledávání"
              className="max-h-[70vh] overflow-y-auto py-1"
              data-testid="global-search-results"
            >
              {categories.map((cat) => (
                <div key={cat.title}>
                  <div
                    className="px-3 pb-1 pt-2 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground"
                    aria-hidden="true"
                  >
                    {cat.title}
                  </div>
                  {cat.items.map((item) => {
                    const index = flatItems.indexOf(item);
                    return (
                      // biome-ignore lint/nursery/useAriaPropsSupportedByRole: aria-selected patří k role="option", Biome čte jen implicitní roli <button>
                      <button
                        key={item.key}
                        type="button"
                        id={`global-search-option-${index}`}
                        // biome-ignore lint/a11y/useSemanticElements: <option> nelze mimo <select>
                        role="option"
                        aria-selected={index === activeIndex}
                        tabIndex={-1}
                        // preventDefault na mousedown — ať input neztratí
                        // focus dřív, než klik stihne vybrat položku.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => select(item)}
                        onMouseEnter={() => setActiveIndex(index)}
                        className={cn(
                          "w-full cursor-pointer px-3 py-2 text-left",
                          index === activeIndex && "bg-muted",
                        )}
                        data-testid={item.testId}
                      >
                        <span className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              "truncate text-sm font-medium",
                              item.zrusen
                                ? "text-muted-foreground line-through"
                                : "text-foreground",
                            )}
                          >
                            {item.label}
                          </span>
                          {item.zrusen ? (
                            <span
                              className="shrink-0 rounded-full bg-[hsl(var(--status-red-bg))] px-1.5 py-0.5 font-medium text-[0.65rem] text-[hsl(var(--status-red-fg))]"
                              data-testid={`${item.testId}-zrusen`}
                            >
                              Zrušen
                            </span>
                          ) : null}
                        </span>
                        {item.sublabel && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {item.sublabel}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
